import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import Home from "./pages/Home";
import Test from "./pages/Test";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Oferta from "./pages/Oferta";
import Footer from "./components/Footer";
import { useState, useEffect } from "react";

// Хук для проверки авторизации
function useAuth() {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		checkAuth();
	}, []);

	const checkAuth = async () => {
		try {
			const response = await fetch('/api/me', {
				method: 'GET',
				credentials: 'include'
			});

			if (response.ok) {
				const data = await response.json();
				setIsAuthenticated(true);
				setUser(data.data.user);
				return true;
			} else {
				setIsAuthenticated(false);
				setUser(null);
				return false;
			}
		} catch (error) {
			console.error('Ошибка проверки авторизации:', error);
			setIsAuthenticated(false);
			setUser(null);
			return false;
		} finally {
			setLoading(false);
		}
	};

	const logout = async () => {
		try {
			await fetch('/api/logout', {
				method: 'POST',
				credentials: 'include'
			});
		} catch (error) {
			console.error('Logout error:', error);
		} finally {
			setIsAuthenticated(false);
			setUser(null);
		}
	};

	return { isAuthenticated, user, setIsAuthenticated, setUser, loading, checkAuth, logout };
}

// Компонент навигации
function Navigation({ isAuthenticated, loading, logout, user, serviceName }) {
	const location = useLocation();
	const navigate = useNavigate();

	const handleLogout = async () => {
		await logout();
		navigate('/');
	};

	// На странице Auth показываем кнопку "Назад"
	if (location.pathname === "/auth") {
		return (
			<nav className="bg-gray-900 p-4 text-white flex justify-between items-center">
				<Link to="/" className="font-bold text-xl">{serviceName}</Link>
				<Link to="/" className="hover:text-gray-300 transition-colors flex items-center gap-2">
					<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
					</svg>
					Назад
				</Link>
			</nav>
		);
	}

	// На странице Dashboard показываем кнопку "Выход"
	if (location.pathname === "/dashboard") {
		return (
			<nav className="bg-gray-900 p-4 text-white flex justify-between items-center">
				<Link to="/" className="font-bold text-xl">{serviceName}</Link>

				{!loading && isAuthenticated ? (
					<button
						onClick={handleLogout}
						className="hover:text-gray-300 transition-colors flex items-center gap-2"
					>
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
						</svg>
						Выход
					</button>
				) : (
					<Link to="/auth" className="hover:text-gray-300 transition-colors flex items-center gap-2">
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
						</svg>
						Вход
					</Link>
				)}
			</nav>
		);
	}

	// На других страницах показываем кнопку в зависимости от авторизации
	return (
		<nav className="bg-gray-900 p-4 text-white flex justify-between items-center">
			<Link to="/" className="font-bold text-xl">{serviceName}</Link>

			{!loading && (
				isAuthenticated ? (
					<Link to="/dashboard" className="hover:text-gray-300 transition-colors flex items-center gap-2">
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
						</svg>
						Личный Кабинет
					</Link>
				) : (
					<Link to="/auth" className="hover:text-gray-300 transition-colors flex items-center gap-2">
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
						</svg>
						Вход
					</Link>
				)
			)}
		</nav>
	);
}

export default function App() {
	const { isAuthenticated, user, setIsAuthenticated, loading, checkAuth, logout } = useAuth();
	const [serviceName, setServiceName] = useState('Shield-AI');

	useEffect(() => {
		async function fetchServiceName() {
			try {
				const response = await fetch('/api/ui/app-name');
				if (response.ok) {
					const data = await response.json();
					setServiceName(data.name || 'Shield-AI');
				}
			} catch (error) {
				console.error('Ошибка загрузки названия сервиса:', error);
			}
		}

		fetchServiceName();
	}, []);

	return (
		<Router>
			<div className="min-h-screen flex flex-col">
				<Navigation
					isAuthenticated={isAuthenticated}
					loading={loading}
					logout={logout}
					user={user}
					serviceName={serviceName}
				/>

				<main className="flex-grow">
					<Routes>
						<Route path="/" element={<Home isAuthenticated={isAuthenticated} user={user} serviceName={serviceName} />} />
						<Route path="/test" element={<Test serviceName={serviceName} />} />
						<Route path="/auth" element={<Auth setIsAuthenticated={setIsAuthenticated} checkAuth={checkAuth} />} />
						<Route path="/reset-password" element={<ResetPassword />} />
						<Route path="/dashboard" element={<Dashboard user={user} logout={logout} checkAuth={checkAuth} serviceName={serviceName} />} />
						<Route path="/oferta" element={<Oferta serviceName={serviceName} />} />
					</Routes>
				</main>

				<Footer serviceName={serviceName} />
			</div>
		</Router>
	);
}
